/**
 * The network reads still in flight, so a stalled cold start can say WHICH
 * read it is waiting on (#1129). The stall report alone only names the gate
 * (`loadStage: org-config`), and the org-config load chains several awaits
 * with no timeout: anon auth, two RTDB `get()`s, a Storage fetch. RTDB reads
 * never show up in a HAR, so without this a field stall is undiagnosable.
 *
 * The client adapters wrap their reads in `trackRead`; `LoadingSplash` reads
 * `pendingReads()` when it reports a stall. Pure (no firebase import), so it
 * unit-tests without the `import.meta` ts-jest trap.
 */

export type PendingRead = { label: string; ageMs: number };

export const createReadTracker = (now: () => number = Date.now) => {
  const inFlight = new Map<number, { label: string; startedAt: number }>();
  let nextId = 0;

  const track = async <T>(label: string, run: () => Promise<T>): Promise<T> => {
    const id = nextId++;
    inFlight.set(id, { label, startedAt: now() });
    try {
      return await run();
    } finally {
      inFlight.delete(id);
    }
  };

  /** Oldest first — the oldest pending read is the likeliest culprit. */
  const pending = (): PendingRead[] => {
    const at = now();
    return [...inFlight.values()]
      .map(({ label, startedAt }) => ({ label, ageMs: at - startedAt }))
      .sort((a, b) => b.ageMs - a.ageMs);
  };

  return { track, pending };
};

const tracker = createReadTracker();

export const trackRead = tracker.track;
export const pendingReads = tracker.pending;
